/**
 * Kickard PHASE 3.3 — lightweight client telemetry.
 *
 * Best-effort fire-and-forget POSTs to /api/telemetry/event. Failures
 * are silently swallowed — telemetry MUST NOT affect gameplay.
 *
 * Uses navigator.sendBeacon (preflight-free text/plain blob) when the
 * tab is unloading; falls back to fetch() with keepalive otherwise.
 *
 * SessionId is a per-tab UUID (resets on hard reload — that's the
 * point, it's a session not a user identity).
 */
import { loadGuest } from './guestIdentity';

// CRA exposes REACT_APP_* vars on `process.env` at build time. Declare
// the minimal shim here so this .ts file compiles without @types/node.
declare const process: { env: { [key: string]: string | undefined } };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'kickard.telemetry.session.v1';

function ensureSessionId(): string {
  // Per-tab via sessionStorage so multiple tabs each have a unique id.
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      let sid = window.sessionStorage.getItem(SESSION_KEY);
      if (!sid) {
        sid = `sess_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
        window.sessionStorage.setItem(SESSION_KEY, sid);
      }
      return sid;
    }
  } catch {
    /* fall through */
  }
  return `sess_${Math.random().toString(36).slice(2, 10)}`;
}

const _sessionId = ensureSessionId();

export function getSessionId(): string {
  return _sessionId;
}

export interface TrackOpts {
  matchId?: string;
  matchType?: string;
  roomCode?: string;
  metadata?: Record<string, unknown>;
  /** When true, prefer navigator.sendBeacon (use on unmount / unload). */
  beacon?: boolean;
}

export function track(eventName: string, opts: TrackOpts = {}): void {
  if (!eventName) return;
  // Phase 3.9A.1 — dedupe noisy screen-level events within a short window
  // so React 18 double-mount / rapid re-renders don't emit spam. The dedupe
  // is scoped to the current session AND resets after 2 seconds — legitimate
  // repeated visits later in the same session are NOT suppressed.
  if (_isNoisyScreenEvent(eventName) && _wasRecentlyEmitted(eventName)) {
    return;
  }
  const guest = loadGuest();
  const payload = {
    eventName,
    guestId: guest?.guestId,
    displayName: guest?.displayName,
    sessionId: _sessionId,
    matchId: opts.matchId,
    matchType: opts.matchType,
    roomCode: opts.roomCode,
    clientTime: new Date().toISOString(),
    metadata: opts.metadata || {},
  };
  const url = `${BACKEND}/api/telemetry/event`;
  const body = JSON.stringify(payload);
  try {
    if (opts.beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch {
    /* swallow */
  }
}

// ─────────────────────────────────────────────────────────────
// Dedupe for noisy screen events (Phase 3.9A.1).
// Fires the same event more than once within DEDUPE_MS ⇒ suppress.
// ─────────────────────────────────────────────────────────────
const DEDUPE_MS = 2000;
const NOISY_SCREEN_EVENTS = new Set<string>([
  'home_loaded',
  'app_open',
  'online_lobby_opened',
  'summary_viewed',
]);
const _lastEmit: Record<string, number> = {};

function _isNoisyScreenEvent(name: string): boolean {
  return NOISY_SCREEN_EVENTS.has(name);
}

function _wasRecentlyEmitted(name: string): boolean {
  const now = Date.now();
  const prev = _lastEmit[name];
  if (prev && (now - prev) < DEDUPE_MS) {
    return true;
  }
  _lastEmit[name] = now;
  return false;
}

// Canonical client-emitted event names (server also has its own).
export const EVT = {
  APP_OPEN: 'app_open',
  APP_BACKGROUNDED: 'app_backgrounded',
  APP_RESUMED: 'app_resumed',
  GUEST_NAME_CREATED: 'guest_name_created',
  GUEST_NAME_EDITED: 'guest_name_edited',
  GUEST_NAME_CHANGED: 'guest_name_edited',
  HOME_LOADED: 'home_loaded',
  HOME_CTA_CLICKED: 'home_cta_clicked',
  ONLINE_LOBBY_OPENED: 'online_lobby_opened',
  BOT_MATCH_STARTED: 'bot_match_started',
  FRIEND_ROOM_CREATED: 'friend_room_created',
  FRIEND_ROOM_JOINED: 'friend_room_joined',
  FRIEND_ROOM_CREATE_FAILED: 'friend_room_create_failed',
  FRIEND_ROOM_JOIN_FAILED: 'friend_room_join_failed',
  QUICK_MATCH_STARTED: 'quick_match_started',
  QUICK_MATCH_CANCELLED: 'quick_match_cancelled',
  QUICK_MATCH_MATCHED: 'quick_match_matched',
  QUICK_MATCH_EXPIRED: 'quick_match_expired',
  QUICK_MATCH_FAILED_OR_EXPIRED: 'quick_match_failed_or_expired',
  MATCH_STARTED: 'match_started',
  MATCH_COMPLETED: 'match_completed',
  MATCH_ABANDONED: 'match_abandoned',
  MATCH_FORFEITED: 'match_forfeited',
  MATCH_RECONNECTED: 'match_reconnected',
  SUMMARY_VIEWED: 'summary_viewed',
  QUEUE_AGAIN_CLICKED: 'queue_again_clicked',
  RETURNED_HOME_AFTER_MATCH: 'returned_home_after_match',
  // Phase 3.11 — Friend Match rematch flow (client-side mirrors).
  REMATCH_REQUEST_CLICKED: 'rematch_request_clicked',
  REMATCH_ACCEPT_CLICKED: 'rematch_accept_clicked',
  REMATCH_DECLINE_CLICKED: 'rematch_decline_clicked',
};
